/**
 * Selection Property Panel — displays building/feature attributes for a 3D selection.
 *
 * Slides into the right rail when a building is selected (from the scene tree or
 * a 3D pick). Presents attributes professionally: hero stats for the headline
 * metrics, a grouped key-attribute list, and a collapsible full-attribute table.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { tokens } from '@/design';
import {
  CODELIST_ATTRIBUTES,
  resolveCodeToLabel,
  type CodelistMap,
  fetchCodelist,
} from '@/components/scene-tree/codelistResolver';

export interface FeatureProperties {
  [key: string]: unknown;
}

export interface SelectionData {
  featureId: string;
  properties: FeatureProperties;
}

export interface SelectionPropertyPanelProps {
  /** Current selection data, null when no selection */
  selection: SelectionData | null;
  /** Multiple selections - shows count and last selected properties */
  selectionCount?: number;
  /** Callback when panel is closed */
  onClose: () => void;
  /** Optional CSS class name */
  className?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Attribute definitions
// ─────────────────────────────────────────────────────────────────────────────

interface AttrDef {
  key: string;
  label: string;
  fallbackKey?: string;
  unit?: string;
  mono?: boolean;
  format?: (value: unknown) => string;
}

/** Headline metrics shown as large stat tiles. */
const HERO_ATTRIBUTES: AttrDef[] = [
  {
    key: 'bldg:measuredHeight',
    fallbackKey: 'measuredHeight',
    label: 'Height',
    unit: 'm',
    format: (v) => `${Number(v).toFixed(1)}`,
  },
  {
    key: 'height',
    label: 'Height',
    unit: 'm',
    format: (v) => `${Number(v).toFixed(1)}`,
  },
  {
    key: 'bldg:storeysAboveGround',
    fallbackKey: 'storeysAboveGround',
    label: 'Stories',
    format: (v) => String(Math.floor(Number(v) || 0)),
  },
];

/** Key attributes shown as a clean label/value list under "Details". */
const DETAIL_ATTRIBUTES: AttrDef[] = [
  { key: 'bldg:usage', fallbackKey: 'usage', label: 'Usage code' },
  { key: 'bldg:class', fallbackKey: 'class', label: 'Class code' },
  { key: 'storeysBelowGround', label: 'Stories below ground' },
  { key: 'yearOfConstruction', label: 'Year built' },
  { key: 'creationDate', label: 'Created' },
  { key: 'roofType', label: 'Roof type' },
  { key: 'bldg:address', fallbackKey: 'address', label: 'Address' },
  {
    key: 'uro:BuildingDetailAttribute_uro:landUseType',
    fallbackKey: 'landUseType',
    label: 'Land use',
  },
  {
    key: 'uro:BuildingDetailAttribute_uro:fireproofStructureType',
    fallbackKey: 'fireproofStructureType',
    label: 'Fire safety',
  },
  {
    key: 'uro:BuildingDetailAttribute_uro:districtsAndZonesType',
    fallbackKey: 'urbanPlanType',
    label: 'Zoning',
  },
  { key: 'lodLevelsPresent', label: 'LOD levels' },
];

/** Identity fields (monospace, shown in the header strip). */
const ID_ATTRIBUTES: AttrDef[] = [
  { key: 'id', fallbackKey: 'gml_id', label: 'Feature ID', mono: true },
  { key: 'gml_id', fallbackKey: 'buildingID', label: 'Feature ID', mono: true },
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function findFloodDepthAttribute(
  properties: FeatureProperties,
): { key: string; value: unknown } | null {
  for (const [key, value] of Object.entries(properties)) {
    if (key.includes('_浸水深')) return { key, value };
  }
  return null;
}

function formatPropertyValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map((v) => String(v)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function getAttributeValue(properties: FeatureProperties, attr: AttrDef): unknown {
  const primary = properties[attr.key];
  if (primary !== undefined && primary !== null && primary !== '') return primary;
  if (attr.fallbackKey) {
    const fb = properties[attr.fallbackKey];
    if (fb !== undefined && fb !== null && fb !== '') return fb;
  }
  return null;
}

/** Humanize a raw attribute key for the full-attribute table. */
function humanizeKey(key: string): string {
  let k = key;
  if (k.startsWith('gen_')) k = k.slice(4); // generic CityGML attribute
  // Drop namespace prefixes like "uro:BuildingDetailAttribute_uro:foo"
  const lastColon = k.lastIndexOf(':');
  if (lastColon >= 0) k = k.slice(lastColon + 1);
  // Keep Japanese labels verbatim; only prettify ASCII camelCase/snake_case.
  // eslint-disable-next-line no-control-regex -- intentional ASCII-range (incl. NUL) check
  if (/^[\x00-\x7F]+$/.test(k)) {
    k = k.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
    k = k.charAt(0).toUpperCase() + k.slice(1);
  }
  return k;
}

// ─────────────────────────────────────────────────────────────────────────────
// Presentational pieces
// ─────────────────────────────────────────────────────────────────────────────

function StatTile({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: tokens.color.surface[1],
        border: tokens.border.thin,
        borderRadius: 8,
        padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <span
        style={{
          fontSize: tokens.font.size.xs,
          color: tokens.color.ink.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span
          style={{
            fontSize: tokens.font.size.lg ?? 18,
            fontWeight: tokens.font.weight.semibold ?? 600,
            color: tokens.color.ink.primary,
            lineHeight: 1.1,
          }}
        >
          {value}
        </span>
        {unit ? (
          <span style={{ fontSize: tokens.font.size.xs, color: tokens.color.ink.secondary }}>
            {unit}
          </span>
        ) : null}
      </span>
    </div>
  );
}

function KeyValueRow({
  label,
  value,
  mono = false,
  zebra = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  zebra?: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '40% 60%',
        gap: tokens.space[2],
        padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
        background: zebra ? tokens.color.surface[1] : 'transparent',
        alignItems: 'start',
      }}
    >
      <span
        style={{
          fontSize: tokens.font.size.xs,
          color: tokens.color.ink.secondary,
          lineHeight: 1.4,
          wordBreak: 'break-word',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: tokens.font.size.sm,
          color: tokens.color.ink.primary,
          lineHeight: 1.4,
          wordBreak: 'break-word',
          fontFamily: mono ? tokens.font.family.mono : undefined,
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
        background: tokens.color.surface[2],
        borderTop: tokens.border.thin,
        borderBottom: tokens.border.thin,
      }}
    >
      <span
        style={{
          fontSize: tokens.font.size.xs,
          fontWeight: tokens.font.weight.semibold ?? 600,
          color: tokens.color.ink.secondary,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {title}
      </span>
      {count != null ? (
        <span style={{ fontSize: tokens.font.size.xs, color: tokens.color.ink.muted }}>
          {count}
        </span>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Panel
// ─────────────────────────────────────────────────────────────────────────────

export function SelectionPropertyPanel({
  selection,
  selectionCount = 1,
  onClose,
}: SelectionPropertyPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    if (selection) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [selection, onClose]);

  useEffect(() => {
    if (selection) {
      setCollapsed(false);
      setShowAll(false);
    }
  }, [selection?.featureId]);

  const properties = selection?.properties ?? {};

  // V3: Codelist resolution — fetch codelists for known coded attributes.
  // The base URL for codelists comes from the selection properties if available.
  const [codelists, setCodelists] = useState<Map<string, CodelistMap>>(new Map());
  const codelistBaseUrl = useMemo(() => {
    const base = properties._codelistBaseUrl as string | undefined;
    return base ?? null;
  }, [properties._codelistBaseUrl]);

  useEffect(() => {
    if (!codelistBaseUrl) return;
    let cancelled = false;
    const needed = new Set<string>();
    for (const [attrKey, filename] of CODELIST_ATTRIBUTES) {
      if (properties[attrKey] !== undefined || (DETAIL_ATTRIBUTES.some((a) => a.key === attrKey || a.fallbackKey === attrKey) && getAttributeValue(properties, { key: attrKey, label: "" }) !== null)) {
        needed.add(filename);
      }
    }
    if (needed.size === 0) return;
    Promise.all(
      [...needed].map(async (filename) => {
        const url = `${codelistBaseUrl.replace(/\/$/, "")}/${filename}`;
        const map = await fetchCodelist(url);
        return map ? { filename, map } : null;
      }),
    ).then((results) => {
      if (cancelled) return;
      const next = new Map<string, CodelistMap>();
      for (const r of results) {
        if (r) next.set(r.filename, r.map);
      }
      setCodelists(next);
    });
    return () => { cancelled = true; };
  }, [codelistBaseUrl, selection?.featureId]);

  /** Resolve a property value through codelists if applicable. */
  const resolveValue = (key: string, value: unknown): string => {
    const filename = CODELIST_ATTRIBUTES.get(key);
    if (!filename || !codelists.has(filename)) return formatPropertyValue(value);
    return resolveCodeToLabel(value as string | number, codelists.get(filename) ?? null);
  };

  const consumedKeys = useMemo(() => {
    const set = new Set<string>();
    for (const a of [...HERO_ATTRIBUTES, ...DETAIL_ATTRIBUTES, ...ID_ATTRIBUTES]) {
      set.add(a.key);
      if (a.fallbackKey) set.add(a.fallbackKey);
    }
    return set;
  }, []);

  if (!selection) return null;

  // Hero stats
  const heroTiles = HERO_ATTRIBUTES.map((attr) => {
    const v = getAttributeValue(properties, attr);
    if (v === null) return null;
    return {
      label: attr.label,
      value: attr.format ? attr.format(v) : String(v),
      unit: attr.unit,
    };
  }).filter(Boolean) as Array<{ label: string; value: string; unit?: string }>;

  // Identity
  const idRows = ID_ATTRIBUTES.map((attr) => {
    const v = getAttributeValue(properties, attr);
    if (v === null) return null;
    return { label: attr.label, value: String(v), mono: attr.mono };
  }).filter(Boolean) as Array<{ label: string; value: string; mono?: boolean }>;

  // Details — with codelist resolution (V3)
  const detailRows = DETAIL_ATTRIBUTES.map((attr) => {
    const v = getAttributeValue(properties, attr);
    if (v === null) return null;
    const resolvedKey = properties[attr.key] !== undefined ? attr.key : (attr.fallbackKey ?? attr.key);
    return { label: attr.label, value: resolveValue(resolvedKey, v), mono: attr.mono };
  }).filter(Boolean) as Array<{ label: string; value: string; mono?: boolean }>;

  const flood = findFloodDepthAttribute(properties);
  if (flood?.value != null) {
    detailRows.push({ label: 'Flood depth (max)', value: `${flood.value} m`, mono: false });
  }

  // Everything else
  const otherRows = Object.entries(properties)
    .filter(([k]) => !consumedKeys.has(k))
    .filter(([k]) => !k.includes('_浸水深'))
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => ({ label: humanizeKey(k), value: formatPropertyValue(v) }));

  const totalCount = Object.keys(properties).length;
  const hasAnything =
    heroTiles.length > 0 || detailRows.length > 0 || otherRows.length > 0;

  const title = idRows[0]?.value ?? selection.featureId;

  return (
    <div
      ref={panelRef}
      role="region"
      aria-label="Selection properties"
      tabIndex={-1}
      data-testid="selection-property-panel"
      style={{
        flexShrink: 0,
        borderBottom: tokens.border.thin,
        background: tokens.color.surface[2],
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
          borderBottom: collapsed ? 'none' : tokens.border.thin,
          minHeight: tokens.density.rowDefault,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2], minWidth: 0 }}>
          <button
            type="button"
            aria-expanded={!collapsed}
            aria-controls="selection-panel-body"
            onClick={() => setCollapsed((c) => !c)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: tokens.color.ink.secondary,
              fontSize: tokens.font.size.xs,
              padding: 0,
              lineHeight: 1,
            }}
            aria-label={collapsed ? 'Expand properties' : 'Collapse properties'}
          >
            {collapsed ? '▶' : '▼'}
          </button>
          <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>
            🏢
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span
              style={{
                fontSize: tokens.font.size.sm,
                fontWeight: tokens.font.weight.semibold ?? 600,
                color: tokens.color.ink.primary,
                lineHeight: 1.2,
              }}
            >
              Building
              {selectionCount > 1 && (
                <span style={{ color: tokens.color.ink.muted, marginLeft: tokens.space[1] }}>
                  ({selectionCount})
                </span>
              )}
            </span>
            <span
              title={title}
              style={{
                fontSize: tokens.font.size.xs,
                fontFamily: tokens.font.family.mono,
                color: tokens.color.ink.muted,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 200,
              }}
            >
              {title}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close properties panel"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: tokens.color.ink.muted,
            fontSize: tokens.font.size.md,
            padding: `0 ${tokens.space[1]}px`,
            lineHeight: 1,
            minHeight: tokens.density.touchMin,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          ×
        </button>
      </div>

      {/* Body */}
      {!collapsed && (
        <div
          id="selection-panel-body"
          style={{ maxHeight: 420, overflowY: 'auto', overflowX: 'hidden' }}
        >
          {!hasAnything ? (
            <div
              style={{
                padding: `${tokens.space[4]}px ${tokens.space[3]}px`,
                textAlign: 'center',
                color: tokens.color.ink.muted,
                fontSize: tokens.font.size.sm,
              }}
            >
              No attributes recorded for this building.
            </div>
          ) : (
            <>
              {/* Hero stats */}
              {heroTiles.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    gap: tokens.space[2],
                    padding: tokens.space[3],
                  }}
                >
                  {heroTiles.map((t) => (
                    <StatTile key={t.label} label={t.label} value={t.value} unit={t.unit} />
                  ))}
                </div>
              )}

              {/* Details */}
              {detailRows.length > 0 && (
                <>
                  <SectionHeader title="Details" />
                  <div>
                    {detailRows.map((r, i) => (
                      <KeyValueRow
                        key={r.label}
                        label={r.label}
                        value={r.value}
                        mono={r.mono}
                        zebra={i % 2 === 1}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* All other attributes */}
              {otherRows.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowAll((s) => !s)}
                    aria-expanded={showAll}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      cursor: 'pointer',
                      border: 'none',
                      background: tokens.color.surface[2],
                      borderTop: tokens.border.thin,
                      borderBottom: showAll ? tokens.border.thin : 'none',
                      padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span
                      style={{
                        fontSize: tokens.font.size.xs,
                        fontWeight: tokens.font.weight.semibold ?? 600,
                        color: tokens.color.ink.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      All attributes ({otherRows.length})
                    </span>
                    <span style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>
                      {showAll ? '−' : '+'}
                    </span>
                  </button>
                  {showAll && (
                    <div>
                      {otherRows.map((r, i) => (
                        <KeyValueRow
                          key={`${r.label}-${i}`}
                          label={r.label}
                          value={r.value}
                          zebra={i % 2 === 1}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Footer count */}
              <div
                style={{
                  padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
                  borderTop: tokens.border.thin,
                  fontSize: tokens.font.size.xs,
                  color: tokens.color.ink.muted,
                  textAlign: 'right',
                }}
              >
                {totalCount} attribute{totalCount === 1 ? '' : 's'} total
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SelectionPropertyPanel;
